/**
 * Pure auth/restore model (E10-F01-T05).
 *
 * Framework-free and side-effect-free, mirroring `router/routes.ts` (pure) vs
 * `router/useRouter.ts` (React). It owns the *decisions* of the session-restore
 * flow so they are unit-testable without a DOM, TanStack Query, or `fetch`:
 *
 *   - `resolveInitialAuth(token, cachedUser)` — given the persisted session
 *     token + (already-validated) cached user, decide the cold-start auth state:
 *       • token AND cached user present  → `restoring` (optimistic: render the
 *         user immediately, then confirm with `GET /auth/me` in the hook);
 *       • token but NO usable cache       → `restoring` (must fetch the user);
 *       • no token                        → `anonymous` (show login).
 *
 *   - `restoreTargetRoute(savedRoute)` — which view to land on after a successful
 *     restore (acceptance: project→openProject, inbox→inbox, else projects).
 *
 * The legacy `auth.js init()` did exactly this in imperative DOM code; here the
 * branching is a pure function and the hook (`useAuth.ts`) performs the effects
 * (validate call, clients/projects load, navigate).
 */
import type { AuthIdentity } from '@eer/shared';
import { DEFAULT_VIEW, pathToRoute, type Route } from '../router/routes.js';

/**
 * Cold-start auth status:
 *   - `restoring`  — a session token exists; we are (re)establishing the user;
 *   - `anonymous`  — no token; the login view is shown.
 * After the hook resolves the restore, status becomes `authenticated` (token +
 * confirmed user) or falls back to `anonymous`.
 */
export type AuthStatus = 'restoring' | 'authenticated' | 'anonymous';

/** The initial decision made from persisted storage on a cold load. */
export interface InitialAuth {
  readonly status: Extract<AuthStatus, 'restoring' | 'anonymous'>;
  /** Optimistically-restored user (present only when a valid cache existed). */
  readonly user: AuthIdentity | null;
  /** True when the hook must call `GET /auth/me` to obtain the user. */
  readonly needsValidation: boolean;
}

/**
 * Decide the cold-start auth state from the persisted token + cached user.
 *
 * A token with a valid cached user → optimistic `restoring` with the user shown
 * immediately (no flash of the login screen on every reload), still confirmed by
 * a background `GET /auth/me`. A token with no usable cache (missing or dropped
 * because it was corrupt — see `getCachedUser`) still tries to restore, but must
 * fetch the user. No token → `anonymous`.
 */
export function resolveInitialAuth(
  token: string | null,
  cachedUser: AuthIdentity | null,
): InitialAuth {
  if (!token) {
    return { status: 'anonymous', user: null, needsValidation: false };
  }
  if (cachedUser) {
    // Optimistic restore: show the cached user now, confirm in the background.
    return { status: 'restoring', user: cachedUser, needsValidation: true };
  }
  // Token but no usable cache (e.g. corrupt blob was dropped) — must fetch /me.
  return { status: 'restoring', user: null, needsValidation: true };
}

/**
 * The route to land on after a successful restore (acceptance: восстанавливается
 * последний маршрут — project→openProject, inbox→inbox, иначе projects).
 *
 *   - a saved `project/:id` with an id → reopen that project detail;
 *   - a saved `inbox` → inbox;
 *   - anything else (incl. a project route with no id, or no saved route) →
 *     the default projects list.
 *
 * The router model already demotes a bare `/project` to the default view, so a
 * saved project route without an id can never restore an empty detail container.
 */
export function restoreTargetRoute(savedRoute: Route | null): Route {
  if (!savedRoute) return { view: DEFAULT_VIEW };
  if (savedRoute.view === 'project') {
    return savedRoute.id ? { view: 'project', id: savedRoute.id } : { view: DEFAULT_VIEW };
  }
  if (savedRoute.view === 'inbox') {
    return { view: 'inbox' };
  }
  return { view: savedRoute.view };
}

/**
 * The route a cold-start restore should land on — the SAME precedence rule the
 * router already uses in `useRouter.resolveInitialRoute`, kept in one pure place
 * so the two cold-route resolvers can never drift apart again (PK-QA-13):
 *
 *   1. an explicit non-root URL wins — a deep-link / bookmark / refresh on a real
 *      path is a stronger intent signal than a stale persisted route, so restore
 *      leaves it in place (`pathToRoute` already demotes garbage / bare `/project`
 *      paths to the default, so an unparseable URL can never blank the shell);
 *   2. else — a bare `/` cold load — fall back to the last saved route via
 *      `restoreTargetRoute` (acceptance: project→openProject, inbox→inbox, else
 *      the projects list).
 *
 * Prior to this the restore flow navigated UNCONDITIONALLY to
 * `restoreTargetRoute(savedRoute)`, clobbering a just-established deep-link a few
 * hundred ms after mount (as soon as `GET /auth/me` resolved). `navigate` is
 * idempotent (no-op when view+id are unchanged), so re-issuing the same deep-link
 * route here is safe.
 */
export function resolveRestoreRoute(currentPath: string, savedRoute: Route | null): Route {
  if (currentPath && currentPath !== '/') {
    return pathToRoute(currentPath);
  }
  return restoreTargetRoute(savedRoute);
}

/**
 * Dependencies the restore orchestration needs, injected so the flow is
 * unit-testable without a DOM / TanStack Query / `fetch`. The React hook
 * (`useAuth.ts`) supplies the real implementations; tests supply fakes.
 */
export interface RestoreDeps {
  /** Persisted session token (null → no session). */
  readonly token: string | null;
  /** Already-validated cached user (null → none / corrupt cache was dropped). */
  readonly cachedUser: AuthIdentity | null;
  /** Confirm the token with the server (`GET /auth/me`). */
  readonly fetchMe: () => Promise<AuthIdentity | null>;
  /** Persist the freshly confirmed user into the session cache. */
  readonly cacheUser: (user: AuthIdentity) => void;
  /** Warm the clients + projects lists (acceptance: "грузятся clients и projects"). */
  readonly loadInitialData: () => Promise<void>;
  /** The last saved route (persisted `eer.route`; drives `restoreTargetRoute`). */
  readonly savedRoute: Route | null;
  /**
   * The path the cold load actually started on (`window.location.pathname`,
   * read by the hook so the model stays DOM-free). An explicit non-root value
   * wins over `savedRoute` — see `resolveRestoreRoute` (PK-QA-13).
   */
  readonly currentPath: string;
  /** Navigate to the restored target route. */
  readonly navigate: (route: Route) => void;
  /** Clear the session everywhere (token + cache) — the 401/logout path. */
  readonly clearSession: () => void;
}

/** Outcome of a cold-start restore. */
export interface RestoreResult {
  /** Final auth status after the restore settles. */
  readonly status: Extract<AuthStatus, 'authenticated' | 'anonymous'>;
  /** The confirmed principal when authenticated, else null. */
  readonly user: AuthIdentity | null;
}

/**
 * Cold-start session-restore orchestration (the imperative heart of the legacy
 * `auth.js init()`), expressed as a single dependency-injected async function so
 * every branch is unit-testable without React.
 *
 * Flow (acceptance criteria 1, 2, 4):
 *   1. no token → `anonymous` (show login), nothing loaded;
 *   2. token (+ optional cached user) → confirm with `GET /auth/me`:
 *        • confirmed → cache the fresh user, warm clients + projects, navigate to
 *          the restored target route, return `authenticated`;
 *        • rejected / errored → clear the session, return `anonymous` (login).
 *
 * A corrupt user cache never reaches here — `getCachedUser` already dropped it
 * and passed `null`, so the flow simply falls through to the `/auth/me` fetch
 * (acceptance: "битый/непарсящийся кэш … без падения → login"). The fetch itself
 * never throws (the api-client returns a value), but we still guard defensively
 * so an unexpected reject can only end in the safe `anonymous` state.
 */
export async function runSessionRestore(deps: RestoreDeps): Promise<RestoreResult> {
  const initial = resolveInitialAuth(deps.token, deps.cachedUser);
  if (initial.status === 'anonymous') {
    return { status: 'anonymous', user: null };
  }

  let user: AuthIdentity | null;
  try {
    user = await deps.fetchMe();
  } catch {
    // The api-client does not throw, but a defensive guard guarantees a failed
    // validation can only ever end in the safe anonymous state.
    user = null;
  }

  if (!user) {
    // Token rejected (401 already self-cleaned via handleUnauthorized) or invalid.
    deps.clearSession();
    return { status: 'anonymous', user: null };
  }

  deps.cacheUser(user);
  await deps.loadInitialData();
  // Precedence: an explicit deep-link URL wins over the stale persisted route,
  // only a bare `/` cold load falls back to the last saved route (PK-QA-13).
  deps.navigate(resolveRestoreRoute(deps.currentPath, deps.savedRoute));
  return { status: 'authenticated', user };
}
